import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SelectedPanelEnum } from '@app/core/enums/selected-panel.enum';
import { PremiumImpactService } from '@app/my-zone/shared/premium-impact-modal/premium-impact.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as calculationActions from '../calculation/calculation.actions';
import { ProductSelectorService } from './product-selector.service';
import * as productsActions from './product.actions';
import { ProductService } from './product.service';

type ClosePanelActionsUnion =
    productsActions.StoreNonRegularDriverSuccess |
    productsActions.CancelFuturePurchasedProductSuccess |
    productsActions.CancelFutureNonRegularDriverSuccess |
    productsActions.CancelFutureNonRegularDriverOwnerSuccess |
    productsActions.TerminateActivePurchasedProductSuccess |
    productsActions.ReviveTerminatedPurchasedProductSuccess |
    productsActions.TerminateActiveNonRegularDriverSuccess |
    productsActions.ModifyFuturePurchasedProductStartDateSuccess |
    productsActions.ModifyTerminationDateForProductEndDateSuccess |
    calculationActions.CancelFuturePurchasedCoverageSuccess |
    calculationActions.ChangeGenericPurchasedCoveragesSuccess;

/** @deprecated use outshared-lib - OutInsurancePolicyModule */
@Injectable()
export class ProductEffects {

     closePanel$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(
            productsActions.STORE_NON_REGULAR_DRIVER_SUCCESS,
            productsActions.CANCEL_FUTURE_PURCHASED_PRODUCT_SUCCESS,
            productsActions.CANCEL_FUTURE_NON_REGULAR_DRIVER_SUCCESS,
            productsActions.TERMINATE_ACTIVE_PURCHASED_PRODUCT_SUCCESS,
            productsActions.REVIVE_TERMINATED_PURCHASED_PRODUCT_SUCCESS,
            productsActions.TERMINATE_ACTIVE_NON_REGULAR_DRIVER_SUCCESS,
            productsActions.MODIFY_FUTURE_PURCHASED_PRODUCT_START_DATE_SUCCESS,
            productsActions.MODIFY_TERMINATION_DATE_FOR_PRODUCT_END_DATE_SUCCESS,
            calculationActions.CANCEL_FUTURE_PURCHASED_COVERAGE_SUCCESS,
            calculationActions.CHANGE_GENERIC_PURCHASED_COVERAGES_SUCCESS,
        ),
        map((action: ClosePanelActionsUnion) => action.payload),
        map((payload) => {
            const { purchased_product_sequence_number } = payload;
            const url = this.router.url.split('?')[0];
            const includedUrls = ['/verzekeringsmap/mijn-verzekeringen'];

            if (!includedUrls.includes(url)) {
                return null;
            }

            this.router.navigate(['/verzekeringsmap/mijn-verzekeringen'], {
                queryParams: {
                    purchased_product: purchased_product_sequence_number,
                    panel: SelectedPanelEnum.Close,
                },
            });

            return null;
        }),
    ), { dispatch: false });

     cancelFuturePurchasedProduct$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(productsActions.CANCEL_FUTURE_PURCHASED_PRODUCT),
        map((action: productsActions.CancelFuturePurchasedProduct) => action.payload),
        switchMap(({ purchased_product_sequence_number }) =>
            this.productService.cancelFuturePurchasedProduct({ purchased_product_sequence_number }).pipe(
                map(() => new productsActions.CancelFuturePurchasedProductSuccess({ purchased_product_sequence_number })),
                catchError((notifications) => of(new productsActions.AddNotifications({ notifications }))),
            ),
        ),
    ));

     terminateActivePurchasedProduct$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(productsActions.TERMINATE_ACTIVE_PURCHASED_PRODUCT),
        map((action: productsActions.TerminateActivePurchasedProduct) => action.payload),
        switchMap(({ end_date, purchased_product_sequence_number }) =>
            this.productService.terminateActivePurchasedProduct({
                end_date,
                purchased_product_sequence_number,
            }).pipe(
                map(() => new productsActions.TerminateActivePurchasedProductSuccess({ end_date, purchased_product_sequence_number })),
                catchError((notifications) => of(new productsActions.AddNotifications({ notifications }))),
            ),
        ),
    ));

     reviveTerminatedPurchasedProduct$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(productsActions.REVIVE_TERMINATED_PURCHASED_PRODUCT),
        switchMap((action: productsActions.ReviveTerminatedPurchasedProduct) =>
            this.productService.reviveTerminatedPurchasedProduct(action.payload).pipe(
                map(() => new productsActions.ReviveTerminatedPurchasedProductSuccess(action.payload)),
                catchError((notifications) => of(new productsActions.AddNotifications({ notifications }))),
            ),
        ),
    ));

     storeNonRegularDriver$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(productsActions.STORE_NON_REGULAR_DRIVER),
        map((action: productsActions.StoreNonRegularDriver) => action.payload),
        switchMap(({ purchased_product_sequence_number, non_regular_driver }) =>
            this.productService.storeNonRegularDriver({ purchased_product_sequence_number, non_regular_driver }).pipe(
                switchMap(() => {
                    return [
                        new productsActions.StoreNonRegularDriverSuccess({ purchased_product_sequence_number, non_regular_driver }),
                        new productsActions.DisplayPremiumImpact({
                            purchased_product_sequence_number,
                            reference_date: non_regular_driver.start_date,
                        }),
                    ];
                }),
                catchError((notifications) => of(new productsActions.AddNotifications({ notifications }))),
            ),
        ),
    ));

     terminateActiveNonRegularDriver$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(productsActions.TERMINATE_ACTIVE_NON_REGULAR_DRIVER),
        map((action: productsActions.TerminateActiveNonRegularDriver) => action.payload),
        switchMap(({ end_date, purchased_product_sequence_number }) =>
            this.productService.terminateActiveNonRegularDriver({
                end_date, purchased_product_sequence_number,
            }).pipe(
                switchMap(() => {
                    return [
                        new productsActions.TerminateActiveNonRegularDriverSuccess({
                            end_date, purchased_product_sequence_number,
                        }),
                        new productsActions.DisplayPremiumImpact({
                            purchased_product_sequence_number,
                            reference_date: end_date,
                        }),
                    ];
                }),
                catchError((notifications) => of(new productsActions.AddNotifications({ notifications }))),
            ),
        ),
    ));

     cancelFutureNonRegularDriver$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(productsActions.CANCEL_FUTURE_NON_REGULAR_DRIVER),
        map((action: productsActions.CancelFutureNonRegularDriver) => action.payload),
        switchMap(({ purchased_product_sequence_number }) =>
            this.productService.cancelFutureNonRegularDriver({ purchased_product_sequence_number }).pipe(
                map(() => new productsActions.CancelFutureNonRegularDriverSuccess({ purchased_product_sequence_number })),
                catchError((notifications) => of(new productsActions.AddNotifications({ notifications }))),
            ),
        ),
    ));

    cancelFutureNonRegularDriverOwner$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(productsActions.CANCEL_FUTURE_NON_REGULAR_DRIVER_OWNER),
        map((action: productsActions.CancelFutureNonRegularDriverOwner) => action.payload),
        switchMap(({ purchased_product_sequence_number }) =>
            this.productService.cancelFutureNonRegularDriver({ purchased_product_sequence_number }).pipe(
                map(() => new productsActions.CancelFutureNonRegularDriverOwnerSuccess({ purchased_product_sequence_number })),
                catchError((notifications) => of(new productsActions.AddNotifications({ notifications }))),
            ),
        ),
    ));

     modifyFuturePurchasedProductStartDate$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(productsActions.MODIFY_FUTURE_PURCHASED_PRODUCT_START_DATE),
        map((action: productsActions.ModifyFuturePurchasedProductStartDate) => action.payload),
        switchMap(({ purchased_product_sequence_number, start_date }) =>
            this.productService.modifyFuturePurchasedProductStartDate({ purchased_product_sequence_number, start_date }).pipe(
                map(() => new productsActions.ModifyFuturePurchasedProductStartDateSuccess({
                    purchased_product_sequence_number, start_date,
                })),
                catchError((notifications) => of(new productsActions.AddNotifications({ notifications }))),
            ),
        ),
    ));

     modifyTerminationDateForProductEndDate$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(productsActions.MODIFY_TERMINATION_DATE_FOR_PRODUCT_END_DATE),
        map((action: productsActions.ModifyTerminationDateForProductEndDate) => action.payload),
        switchMap(({ purchased_product_sequence_number, end_date }) =>
            this.productService.modifyTerminationDateForProductEndDate({
                purchased_product_sequence_number, end_date,
            }).pipe(
                map(() => new productsActions.ModifyTerminationDateForProductEndDateSuccess({
                    purchased_product_sequence_number, end_date,
                })),
                catchError((notifications) => of(new productsActions.AddNotifications({ notifications }))),
            ),
        ),
    ));

     cancelFutureModifiablePremiumDeterminingFactor$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(productsActions.CANCEL_FUTURE_MODIFIABLE_PREMIUM_DETERMINING_FACTOR),
        map((action: productsActions.CancelFutureModifiablePremiumDeterminingFactor) => action.payload),
        switchMap(({ premium_determining_factor_id, purchased_product_sequence_number }) =>
            this.productService.cancelFutureModifiablePremiumDeterminingFactor({
                premium_determining_factor_id,
                purchased_product_sequence_number,
            }).pipe(
                map(() => new productsActions.CancelFutureModifiablePremiumDeterminingFactorSuccess({
                    premium_determining_factor_id,
                    purchased_product_sequence_number,
                })),
                catchError((notifications) => of(new productsActions.AddNotifications({ notifications }))),
            ),
        ),
    ));

    terminateProductSuccess$: Observable<void> = createEffect(() => this.actions$.pipe(
        ofType(productsActions.TERMINATE_ACTIVE_PURCHASED_PRODUCT_SUCCESS),
        map((action: productsActions.TerminateActivePurchasedProduct) => action.payload),
        withLatestFrom(this.productSelectorService.getProductEntities$),
        map(([payload, products]) => {
            const { end_date } = payload;
            const product = products[payload.purchased_product_sequence_number];

            // TODO: WINS-7245 show feedback modal after product termination
            // if (product.product_id === ProductId.home) {
            //     return this.insuranceModalService.newHomeFeedbackModal({ end_date, product });
            // }
            // return this.insuranceModalService.cancelProductFeedbackModal({ end_date, product });
        }),
    ), { dispatch: false });

     displayPremiumImpact$: Observable<any> = createEffect(() => this.actions$.pipe(
        ofType(productsActions.DISPLAY_PREMIUM_IMPACT),
        map((action: productsActions.DisplayPremiumImpact) =>
            this.premiumImpactService.getChanges(
                action.payload.reference_date,
                action.payload.purchased_product_sequence_number),
        ),
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private productService: ProductService,
        private router: Router,
        private productSelectorService: ProductSelectorService,
        private premiumImpactService: PremiumImpactService,
    ) {}
}
